import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiDirectory } from '@core/api/api-directory';
import { RequestResponse } from '@core/services/request/model/request.model';
import { AuthUserApi } from '@core/api/model/user/auth-user-api.model';
import { UserApi } from '@core/api/model/user/user-api.model';
import { HttpParams } from '@angular/common/http';
import { UsersListingAPI, UsersListingFilterApi } from '@core/api/model/user/users-listing-api.model';
import { LoadingIds } from '@shared/constants/loading';
import { HttpParamsService } from '@core/services/http-params/http-params.service';
import {
  NewUserRequestApi,
  ResetPasswordRequestApi,
  UserrAccessesRequestApi,
  UserRequestApi,
} from '@core/api/model/user/user-request-api.model';
import { RequestService } from '@core/services/request/request.service';
import { UserCountry } from '@core/domain/model/user/auth-user.model';

@Injectable({ providedIn: 'root' })
export class ApiUserService {
  constructor(
    private _requestService: RequestService,
    private _httpParamsService: HttpParamsService
  ) {}

  /**
   * Fetch user detail
   */
  public fetchIndividualUserDetail(email: string, idHashCRM: string): Observable<RequestResponse<UserApi>> {
    let params = new HttpParams();
    params = params.append('email', email);
    params = params.append('idHashCRM', idHashCRM);
    return this._requestService.get<RequestResponse<UserApi>>(ApiDirectory.User.UserDetail({}), { params });
  }
  /**
   * Fetch user
   */
  public fetchUser(): Observable<RequestResponse<Array<AuthUserApi>>> {
    return this._requestService.get(ApiDirectory.User.AuthUser({}), { loading: true, bypassCache: true });
  }

  public updateUser(body: UserRequestApi, loading?: LoadingIds): Observable<RequestResponse<boolean>> {
    return this._requestService.put<RequestResponse<boolean>, UserRequestApi>(ApiDirectory.User.UpdateUser({}), body, {
      loading: loading,
    });
  }

  public addNewUser(body: NewUserRequestApi, loading?: LoadingIds): Observable<RequestResponse<boolean>> {
    return this._requestService.post<RequestResponse<boolean>, NewUserRequestApi>(ApiDirectory.User.AddNewUser({}), body, {
      loading: loading,
    });
  }

  public updateUserAccess(body: UserrAccessesRequestApi): Observable<RequestResponse<boolean>> {
    return this._requestService.put<RequestResponse<boolean>, UserrAccessesRequestApi>(ApiDirectory.User.UpdateUserAccess({}), body);
  }

  public fetchUsers(filter: UsersListingFilterApi): Observable<RequestResponse<Array<UsersListingAPI>>> {
    return this._requestService.get(ApiDirectory.User.UsersVisibles({}), {
      params: this._httpParamsService.toParams(filter),
      hideErrorAlert: true,
    });
  }

  public removeUser(userId: string): Observable<RequestResponse<boolean>> {
    return this._requestService.delete(ApiDirectory.User.EliminarUsuario({ idHashCRM: userId }), {
      loading: LoadingIds.REMOVE_USER,
    });
  }

  public impersonateUser(impersonationEmail: string): Observable<
    RequestResponse<{
      userToken: string;
    }>
  > {
    return this._requestService.get(ApiDirectory.User.ChangeImpersonalizedUser({}), {
      params: this._httpParamsService.toParams({ MailUserToImpersonalize: impersonationEmail }),
      loading: LoadingIds.IMPERSONATION,
    });
  }
  public resetPasswordRequest(body: ResetPasswordRequestApi): Observable<RequestResponse<boolean>> {
    return this._requestService.post(ApiDirectory.User.resetPassword({}), body);
  }

  public emailWelcome(mail: string): Observable<RequestResponse<boolean>> {
    const params = new HttpParams().set('mail', mail);
    return this._requestService.post(ApiDirectory.User.emailWelcome({}), {}, { params: params, loading: LoadingIds.REGISTER });
  }

  public putDataProtection(value: boolean, country: UserCountry): Observable<RequestResponse<boolean>> {
    return this._requestService.put(
      ApiDirectory.User.ProteccionDatos({}),
      {
        proteccionDatos: value,
        pais: country,
      },
      {
        loading: LoadingIds.PUT_DATA_PROTECTION,
      }
    );
  }

  public updateCommunicationLanguage(lang: string): Observable<RequestResponse<boolean>> {
    return this._requestService.put(ApiDirectory.User.IdiomaComunicacion({}), {
      idiomaComunicacion: lang,
    });
  }
}
